// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { RoleProvider } from './contexts/RoleContext';

import AuthRedirect from './components/AuthRedirect';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import DebugInfo from './components/debug/DebugInfo';

import { Toaster } from 'react-hot-toast';

// Public pages
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegistrationPage from './pages/RegistrationPage';
import BrokerRegistrationPage from './pages/BrokerRegistrationPage';       // renamed
import CarrierRegistrationPage from './pages/CarrierRegistrationPage';     // renamed
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import NotFoundPage from './pages/NotFoundPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailVerification from './components/auth/EmailVerification';

// Dashboards
import CarrierDashboardPage from './pages/CarrierDashboardPage';           // renamed
import CarrierOnboardingPage from './pages/CarrierOnboardingPage';         // renamed
import CarrierProfileEditPage from './pages/CarrierProfileEditPage';

// Admin pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminDocumentsPage from './pages/admin/AdminDocumentsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminExportPage from './pages/admin/AdminExportPage';
import AdminLogsPage from './pages/admin/AdminLogsPage';

// Broker pages
import CheckoutPage from './pages/CheckoutPage';
import BrokerLayout from './components/broker/BrokerLayout';               // renamed
import BrokerDashboardPage from './pages/broker/BrokerDashboardPage';      // renamed
import BrokerCarriersPage from './pages/broker/BrokerCarriersPage';        // renamed
import BrokerDocumentsPage from './pages/broker/BrokerDocumentsPage';      // renamed
import BrokerAgreementsPage from './pages/broker/BrokerAgreementsPage';
import BrokerSettingsPage from './pages/broker/BrokerSettingsPage';

function App() {
  return (
    <Router>
      <Toaster position="top-right" />

      <NotificationProvider>
        <AuthProvider>
          <AuthRedirect />

          <RoleProvider>
            {process.env.NODE_ENV === 'development' && <DebugInfo />}

            <div className="app-container min-h-screen flex flex-col">
              <Routes>

                {/* 🌐 Public routes */}
                <Route path="/" element={<Layout><LandingPage /></Layout>} />
                <Route path="/features" element={<Layout><FeaturesPage /></Layout>} />
                <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/about" element={<Layout><AboutPage /></Layout>} />
                <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
                <Route path="/login" element={<Layout><LoginPage /></Layout>} />
                <Route path="/register" element={<Layout><RegistrationPage /></Layout>} />
                <Route path="/register/broker" element={<Layout><BrokerRegistrationPage /></Layout>} />
                <Route path="/register/carrier" element={<Layout><CarrierRegistrationPage /></Layout>} />
                <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
                <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<Layout><ResetPasswordPage /></Layout>} />
                <Route path="/verify-email" element={<Layout><EmailVerification /></Layout>} />
                <Route path="/verify-email/:token" element={<Layout><EmailVerification /></Layout>} />

                {/* 🚛 Carrier routes */}
                <Route
                  path="/carrier/*"
                  element={
                    <ProtectedRoute>
                      <CarrierDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/carrier/onboarding"
                  element={
                    <ProtectedRoute>
                      <CarrierOnboardingPage />
                    </ProtectedRoute>
                  }
                />
<Route path="/carrier/profile/edit" element={<CarrierProfileEditPage />} />
                {/* 🏢 Broker routes */}
                <Route
                  path="/broker"
                  element={
                    <ProtectedRoute>
                      <BrokerLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<BrokerDashboardPage />} />
                  <Route path="carriers" element={<BrokerCarriersPage />} />
                  <Route path="documents" element={<BrokerDocumentsPage />} />
                  <Route path="agreements" element={<BrokerAgreementsPage />} />
                  <Route path="settings" element={<BrokerSettingsPage />} />
                </Route>

                {/* 🛡️ Platform Admin routes */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminDashboardPage />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminUsersPage />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/export"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminExportPage />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/logs"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLogsPage />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                {/* ❌ 404 */}
                <Route path="*" element={<Layout><NotFoundPage /></Layout>} />

              </Routes>
            </div>
          </RoleProvider>
        </AuthProvider>
      </NotificationProvider>
    </Router>
  );
}

export default App;